import { useCallback, useEffect, useState } from "react";

/**
 * Generic hook for managing URL query parameters
 */
export const useUrlParams = () => {
  const [params, setParamsState] = useState<URLSearchParams>(() => {
    return new URLSearchParams(window.location.search);
  });

  // Update internal state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setParamsState(new URLSearchParams(window.location.search));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Get a specific parameter value
   */
  const getParam = useCallback((key: string): string | null => {
    return params.get(key);
  }, [params]);

  /**
   * Set a parameter and update the URL
   */
  const setParam = useCallback((key: string, value: string | null, replaceHistory: boolean = true) => {
    // Always read from current URL to ensure we have the latest parameters
    const currentParams = new URLSearchParams(window.location.search);
    
    if (value === null || value === '') {
      currentParams.delete(key);
    } else {
      currentParams.set(key, value);
    }

    const newUrl = `${window.location.pathname}${currentParams.toString() ? '?' + currentParams.toString() : ''}`;
    
    if (replaceHistory) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }

    setParamsState(new URLSearchParams(currentParams));
  }, []);

  /**
   * Set multiple parameters at once
   */
  const setParams = useCallback((updates: Record<string, string | null>, replaceHistory: boolean = true) => {
    // Always read from current URL to ensure we have the latest parameters
    const currentParams = new URLSearchParams(window.location.search);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        currentParams.delete(key);
      } else {
        currentParams.set(key, value);
      }
    });

    const newUrl = `${window.location.pathname}${currentParams.toString() ? '?' + currentParams.toString() : ''}`;
    
    if (replaceHistory) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }

    setParamsState(new URLSearchParams(currentParams));
  }, []); // Remove params dependency since we're reading from window.location.search

  /**
   * Remove a parameter
   */
  const removeParam = useCallback((key: string, replaceHistory: boolean = true) => {
    setParam(key, null, replaceHistory);
  }, [setParam]);

  return {
    params,
    getParam,
    setParam,
    setParams,
    removeParam,
  };
};